import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import * as React from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

import groupService from '@/services/group.service'

import { CreateChannelForm } from './channel-management-dialogs'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs'

import { GroupChannel, GroupStatus } from '@/types/group-channel.type'

interface EditGroupDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  group: GroupChannel | null
}

interface DeleteGroupDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  group: GroupChannel | null
}

export function EditGroupDialog({ open, onOpenChange, group }: EditGroupDialogProps) {
  const queryClient = useQueryClient()
  const [name, setName] = React.useState('')
  const [status, setStatus] = React.useState<GroupStatus>('PUBLIC')

  React.useEffect(() => {
    if (group) {
      setName(group.name)
      setStatus(group.status)
    }
  }, [group])

  const updateGroupMutation = useMutation({
    mutationFn: (data: { name: string; status: GroupStatus }) => {
      if (!group) throw new Error('Group not found')
      return groupService.updateGroup(group.id, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forum-groups'] })
      queryClient.invalidateQueries({ queryKey: ['meme-post-list'] })
      onOpenChange(false)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!group || !name.trim()) return
    updateGroupMutation.mutate({ name: name.trim(), status })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Chỉnh sửa nhóm</DialogTitle>
          <DialogDescription>Cập nhật thông tin nhóm. Nhấn lưu khi hoàn tất.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                Tên nhóm
              </Label>
              <Input
                id="name"
                value={name}
                onChange={e => setName(e.target.value)}
                className="col-span-3"
                placeholder="Nhập tên nhóm..."
                required
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="status" className="text-right">
                Trạng thái
              </Label>
              <Select value={status} onValueChange={(value: GroupStatus) => setStatus(value)}>
                <SelectTrigger className="col-span-3">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PUBLIC" className="hover:bg-accent hover:text-accent-foreground">
                    Công khai
                  </SelectItem>
                  <SelectItem value="PRIVATE" className="hover:bg-accent hover:text-accent-foreground">
                    Riêng tư
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Hủy
            </Button>
            <Button type="submit" disabled={updateGroupMutation.isPending || !name.trim()}>
              {updateGroupMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Lưu thay đổi
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

interface CreateGroupOrChannelDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CreateGroupOrChannelDialog({ open, onOpenChange }: CreateGroupOrChannelDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal>
      <DialogContent className="sm:max-w-[425px]">
        <Tabs defaultValue="create-group">
          <TabsList>
            <TabsTrigger value="create-group">Tạo nhóm</TabsTrigger>
            <TabsTrigger value="create-channel">Tạo kênh</TabsTrigger>
          </TabsList>
          <TabsContent value="create-group">
            <DialogHeader>
              <DialogTitle>Tạo nhóm mới</DialogTitle>
            </DialogHeader>
            <CreateGroupForm onOpenChange={onOpenChange} />
          </TabsContent>
          <TabsContent value="create-channel">
            <DialogHeader>
              <DialogTitle>Tạo kênh mới</DialogTitle>
            </DialogHeader>
            <CreateChannelForm onOpenChange={onOpenChange} group={null} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}

function CreateGroupForm({ onOpenChange }: { onOpenChange: (open: boolean) => void }) {
  const queryClient = useQueryClient()
  const [name, setName] = React.useState('')
  const [status, setStatus] = React.useState<GroupStatus>('PUBLIC')

  const createGroupMutation = useMutation({
    mutationFn: (data: { name: string; status: GroupStatus }) => groupService.createGroup(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forum-groups'] })
      queryClient.invalidateQueries({ queryKey: ['meme-post-list'] })
      setName('')
      setStatus('PUBLIC')
      onOpenChange(false)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    createGroupMutation.mutate({ name: name.trim(), status })
  }
  return (
    <form onSubmit={handleSubmit}>
      <div className="grid gap-4 py-4">
        <div className="grid grid-cols-4 items-center gap-4">
          <Label htmlFor="create-name" className="text-right">
            Tên nhóm
          </Label>
          <Input
            id="create-name"
            value={name}
            onChange={e => setName(e.target.value)}
            className="col-span-3"
            placeholder="Nhập tên nhóm..."
            required
          />
        </div>
        <div className="grid grid-cols-4 items-center gap-4">
          <Label htmlFor="create-status" className="text-right">
            Trạng thái
          </Label>
          <Select value={status} onValueChange={(value: GroupStatus) => setStatus(value)}>
            <SelectTrigger className="col-span-3">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="PUBLIC" className="hover:bg-accent hover:text-accent-foreground">
                Công khai
              </SelectItem>
              <SelectItem value="PRIVATE" className="hover:bg-accent hover:text-accent-foreground">
                Riêng tư
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          Hủy
        </Button>
        <Button type="submit" disabled={createGroupMutation.isPending || !name.trim()}>
          {createGroupMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Tạo nhóm
        </Button>
      </DialogFooter>
    </form>
  )
}

export function DeleteGroupDialog({ open, onOpenChange, group }: DeleteGroupDialogProps) {
  const queryClient = useQueryClient()

  const deleteGroupMutation = useMutation({
    mutationFn: () => {
      if (!group) throw new Error('Group not found')
      return groupService.deleteGroup(group.id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forum-groups'] })
      queryClient.invalidateQueries({ queryKey: ['meme-post-list'] })
      onOpenChange(false)
    },
  })

  const handleDelete = () => {
    if (!group) return
    deleteGroupMutation.mutate()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Xóa nhóm</DialogTitle>
          <DialogDescription>
            Bạn có chắc chắn muốn xóa nhóm "{group?.name}"? Hành động này không thể hoàn tác và sẽ xóa tất cả các kênh
            trong nhóm.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Hủy
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={deleteGroupMutation.isPending}>
            {deleteGroupMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Xóa nhóm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
